import React, { useState, useEffect, useCallback } from 'react';
import {
    BarChart3, ArrowLeft, RefreshCw, Loader2, AlertTriangle,
    Eye, Users, Zap, ShieldCheck, Server, Heart, MessageSquare,
    Activity, BarChart2, Lightbulb, DollarSign, DatabaseZap, Bug,
    ChevronDown, X,
} from 'lucide-react';
import { adminAnalytics, isAdmin } from '../../services/api';

// Tab components
import OverviewTabV2 from './OverviewTabV2';
import TeacherValueTab from './TeacherValueTab';
import LessonChatTab from './LessonChatTab';
import LiveOpsTab from './LiveOpsTab';
import RetentionTabV2 from './RetentionTabV2';
import FeatureUsageTabV2 from './FeatureUsageTabV2';
import FeedbackInsightsTab from './FeedbackInsightsTab';
import MonetizationTabV2 from './MonetizationTabV2';
import EventExplorerTabV2 from './EventExplorerTabV2';

// ─── Tab definitions ────────────────────────────────────────────────────────
// Order: Overview → Live Ops (action-critical) → Teacher Value → other analytics
const TABS = [
    { id: 'overview',         label: 'Overview',            icon: Eye,           desc: 'How are we doing right now?' },
    { id: 'live-ops',         label: 'Live Ops',            icon: Activity,      desc: 'What is breaking right now?' },
    { id: 'teacher-value',    label: 'Teacher Value',       icon: Heart,         desc: 'Are teachers getting value?' },
    { id: 'lesson-chat',      label: 'Lesson Chat',         icon: MessageSquare, desc: 'Is Lesson Chat useful and sustainable?' },
    { id: 'retention',        label: 'Retention',           icon: Users,         desc: 'Are teachers coming back?' },
    { id: 'feature-usage',    label: 'Feature Usage',       icon: BarChart2,     desc: 'Which features are earning usage?' },
    { id: 'feedback-insights',label: 'Signals & Insights',  icon: Lightbulb,     desc: 'What needs action next?' },
    { id: 'monetization',     label: 'Monetization',        icon: DollarSign,    desc: 'Where is monetization improving?' },
    { id: 'event-explorer',   label: 'Event Explorer',      icon: DatabaseZap,   desc: 'What exactly happened?' },
];

const DATE_RANGES = [
    { value: 1,  label: '24h' },
    { value: 7,  label: '7d' },
    { value: 30, label: '30d' },
    { value: 90, label: '90d' },
];

// ─── Main Dashboard ─────────────────────────────────────────────────────────
const AdminAnalyticsDashboard = ({ onBack }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [days, setDays] = useState(7);
    const [districtId, setDistrictId] = useState('');
    const [campusId, setCampusId] = useState('');
    const [featureArea, setFeatureArea] = useState('');
    const [deployVersion, setDeployVersion] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    // Summary strip data
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(true);

    const filters = {
        days,
        district_id: districtId || undefined,
        campus_id: campusId || undefined,
        feature_area: featureArea || undefined,
        deployment_version: deployVersion || undefined,
    };

    const loadSummary = useCallback(async () => {
        setSummaryLoading(true);
        try {
            const [opsRes, overviewRes, watchRes] = await Promise.allSettled([
                adminAnalytics.getOpsSummary(),
                adminAnalytics.getOverview({ days }),
                adminAnalytics.getWatchItems({ days }),
            ]);

            const ops = opsRes.status === 'fulfilled' ? opsRes.value.data : {};
            const overview = overviewRes.status === 'fulfilled' ? overviewRes.value.data : {};
            const watch = watchRes.status === 'fulfilled' ? watchRes.value.data : {};

            setSummary({
                deployment_version: ops.deployment_version || null,
                watch_item_count: watch.total || watch.items?.length || 0,
                active_users: overview.users?.active_last_n_days ?? 0,
                lessons_generated: overview.engagement?.lessons_generated ?? 0,
                pipeline_success_rate: ops.pipeline_health_24h?.success_rate_pct ?? null,
                open_bugs: overview.feedback?.open_bugs ?? 0,
            });
        } catch {
            setSummary(null);
        } finally {
            setSummaryLoading(false);
        }
    }, [days]);

    useEffect(() => { loadSummary(); }, [loadSummary, refreshKey]);

    const handleRefresh = () => setRefreshKey(k => k + 1);

    if (!isAdmin()) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: '100vh', flexDirection: 'column', gap: 16,
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            }}>
                <div style={{
                    width: 56, height: 56, borderRadius: 16,
                    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 14px rgba(99,102,241,0.25)',
                }}>
                    <BarChart3 size={26} style={{ color: 'white' }} />
                </div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: 0 }}>Saige Analytics</h2>
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 20px', borderRadius: 12,
                    background: '#fef2f2', border: '1px solid #fecaca',
                }}>
                    <ShieldCheck size={16} style={{ color: '#ef4444' }} />
                    <span style={{ fontSize: 14, color: '#991b1b', fontWeight: 500 }}>Access denied — admin role required</span>
                </div>
                <p style={{ fontSize: 13, color: '#64748b', maxWidth: 340, textAlign: 'center', margin: 0, lineHeight: 1.5 }}>
                    This dashboard is restricted to district and campus administrators.
                    If you believe you should have access, contact your administrator.
                </p>
                {onBack ? (
                    <button
                        onClick={onBack}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '8px 18px', borderRadius: 8,
                            background: '#6366f1', color: 'white', border: 'none',
                            fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            marginTop: 8,
                        }}
                    >
                        <ArrowLeft size={14} /> Back to Dashboard
                    </button>
                ) : (
                    <button
                        onClick={() => { window.location.href = '/'; }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '8px 18px', borderRadius: 8,
                            background: '#6366f1', color: 'white', border: 'none',
                            fontSize: 13, fontWeight: 600, cursor: 'pointer',
                            marginTop: 8,
                        }}
                    >
                        <ArrowLeft size={14} /> Go to Dashboard
                    </button>
                )}
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
            {/* ── Header ───────────────────────────────────────────────────── */}
            <div style={{
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                padding: '20px 32px 0',
                borderBottom: '1px solid #334155',
            }}>
                {/* Top bar */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        {onBack && (
                            <button
                                onClick={onBack}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                            >
                                <ArrowLeft size={20} style={{ color: '#94a3b8' }} />
                            </button>
                        )}
                        <div style={{
                            width: 36, height: 36, borderRadius: 10,
                            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <BarChart3 size={18} style={{ color: 'white' }} />
                        </div>
                        <div>
                            <h1 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>
                                Saige Analytics
                            </h1>
                            <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>
                                Decision dashboard for product leadership
                            </p>
                        </div>
                        <span style={{
                            fontSize: 10, fontWeight: 600, color: '#818cf8',
                            background: 'rgba(99,102,241,0.15)', padding: '2px 8px',
                            borderRadius: 6, border: '1px solid rgba(99,102,241,0.25)',
                            marginLeft: 8,
                        }}>ADMIN</span>
                    </div>

                    {/* Global filter controls */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {/* Date range */}
                        <div style={{ display: 'flex', gap: 2, background: '#1e293b', borderRadius: 8, padding: 2 }}>
                            {DATE_RANGES.map(r => (
                                <button
                                    key={r.value}
                                    onClick={() => setDays(r.value)}
                                    style={{
                                        padding: '5px 10px', borderRadius: 6, border: 'none',
                                        fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                        background: days === r.value ? '#6366f1' : 'transparent',
                                        color: days === r.value ? 'white' : '#64748b',
                                        transition: 'all 0.15s',
                                    }}
                                >
                                    {r.label}
                                </button>
                            ))}
                        </div>

                        {/* Filter inputs */}
                        <FilterInput placeholder="District" value={districtId} onChange={setDistrictId} width={90} />
                        <FilterInput placeholder="Campus" value={campusId} onChange={setCampusId} width={80} />
                        <FilterInput placeholder="Feature" value={featureArea} onChange={setFeatureArea} width={80} />
                        <FilterInput placeholder="Deploy" value={deployVersion} onChange={setDeployVersion} width={80} />

                        {/* Refresh */}
                        <button
                            onClick={handleRefresh}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                padding: '6px 12px', borderRadius: 8,
                                background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.25)',
                                color: '#a5b4fc', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            <RefreshCw size={12} /> Refresh
                        </button>
                    </div>
                </div>

                {/* ── Summary Strip ────────────────────────────────────── */}
                <SummaryStrip data={summary} loading={summaryLoading} onTabChange={setActiveTab} />

                {/* ── Tab Navigation ───────────────────────────────────── */}
                <div style={{
                    display: 'flex', gap: 2, overflowX: 'auto',
                    paddingBottom: 0, marginTop: 8,
                }}>
                    {TABS.map(tab => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                id={`tab-${tab.id}`}
                                onClick={() => setActiveTab(tab.id)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '10px 14px', border: 'none', cursor: 'pointer',
                                    borderBottom: isActive ? '2px solid #818cf8' : '2px solid transparent',
                                    background: isActive ? 'rgba(99,102,241,0.08)' : 'transparent',
                                    borderRadius: '8px 8px 0 0',
                                    color: isActive ? '#e0e7ff' : '#64748b',
                                    fontSize: 12, fontWeight: isActive ? 600 : 500,
                                    whiteSpace: 'nowrap',
                                    transition: 'all 0.15s',
                                }}
                                title={tab.desc}
                            >
                                <Icon size={14} />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Tab Content ──────────────────────────────────────────── */}
            <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 32px 60px' }}>
                {activeTab === 'overview' && <OverviewTabV2 key={refreshKey} filters={filters} onTabChange={setActiveTab} />}
                {activeTab === 'live-ops' && <LiveOpsTab key={refreshKey} filters={filters} />}
                {activeTab === 'teacher-value' && <TeacherValueTab key={refreshKey} filters={filters} />}
                {activeTab === 'lesson-chat' && <LessonChatTab key={refreshKey} filters={filters} />}
                {activeTab === 'retention' && <RetentionTabV2 key={refreshKey} filters={filters} />}
                {activeTab === 'feature-usage' && <FeatureUsageTabV2 key={refreshKey} filters={filters} />}
                {activeTab === 'feedback-insights' && <FeedbackInsightsTab key={refreshKey} filters={filters} onTabChange={setActiveTab} />}
                {activeTab === 'monetization' && <MonetizationTabV2 key={refreshKey} filters={filters} />}
                {activeTab === 'event-explorer' && <EventExplorerTabV2 key={refreshKey} filters={filters} />}
            </div>

            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};


// ─── Summary Strip ──────────────────────────────────────────────────────────

// ─── Decision Strip — always-visible health bar ─────────────────────────────

const SummaryStrip = ({ data, loading, onTabChange }) => {
    if (loading && !data) {
        return (
            <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', background: 'rgba(255,255,255,0.03)',
                borderRadius: 8, marginBottom: 4,
            }}>
                <Loader2 size={13} style={{ animation: 'spin 1s linear infinite', color: '#6366f1' }} />
                <span style={{ fontSize: 11, color: '#475569' }}>Loading decision strip…</span>
            </div>
        );
    }
    if (!data) return null;

    const successRate = data.pipeline_success_rate;
    const successColor = successRate == null ? '#64748b'
        : successRate >= 99 ? '#22c55e'
        : successRate >= 95 ? '#f59e0b' : '#ef4444';
    const successBg = successRate == null ? 'transparent'
        : successRate >= 99 ? 'rgba(34,197,94,0.1)'
        : successRate >= 95 ? 'rgba(245,158,11,0.1)' : 'rgba(239,68,68,0.1)';

    const watchCount = data.watch_item_count;
    const watchColor = watchCount > 0 ? '#f59e0b' : '#22c55e';
    const watchBg = watchCount > 0 ? 'rgba(245,158,11,0.1)' : 'rgba(34,197,94,0.06)';

    const openBugs = data.open_bugs ?? 0;
    const bugColor = openBugs > 0 ? '#ef4444' : '#22c55e';
    const bugBg = openBugs > 0 ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.06)';

    const pillBase = {
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '6px 12px', borderRadius: 8, cursor: 'default',
        border: '1px solid rgba(255,255,255,0.06)',
    };

    return (
        <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            padding: '10px 16px', background: 'rgba(255,255,255,0.03)',
            borderRadius: 8, marginBottom: 4,
            flexWrap: 'wrap',
        }}>
            {/* Deploy version — informational */}
            <div style={{ ...pillBase, background: 'rgba(255,255,255,0.04)', cursor: 'default' }}
                title={data.deployment_version ? `Deploy: ${data.deployment_version}` : 'Set CURRENT_DEPLOYMENT_VERSION env var'}>
                <Server size={12} color="#64748b" />
                <span style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>deploy:</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: data.deployment_version ? '#a5f3fc' : '#475569', fontFamily: 'monospace' }}>
                    {data.deployment_version || 'not set'}
                </span>
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.08)' }} />

            {/* Pipeline health — clickable */}
            <div
                style={{ ...pillBase, background: successBg, cursor: onTabChange ? 'pointer' : 'default' }}
                onClick={() => onTabChange && onTabChange('live-ops')}
                title="Pipeline health · click to open Live Ops"
            >
                <ShieldCheck size={13} color={successColor} />
                <span style={{ fontSize: 10, color: '#94a3b8' }}>Pipeline</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: successColor }}>
                    {successRate != null ? `${successRate}%` : '—'}
                </span>
                {successRate != null && successRate < 99 && (
                    <span style={{ fontSize: 9, color: successColor, fontWeight: 700 }}>⚠</span>
                )}
            </div>

            {/* Watch items — clickable */}
            <div
                style={{ ...pillBase, background: watchBg, cursor: onTabChange ? 'pointer' : 'default' }}
                onClick={() => watchCount > 0 && onTabChange && onTabChange('live-ops')}
                title={watchCount > 0 ? 'Watch items firing · click to review in Live Ops' : 'No watch items'}
            >
                <AlertTriangle size={13} color={watchColor} />
                <span style={{ fontSize: 10, color: '#94a3b8' }}>Watch</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: watchColor }}>{watchCount}</span>
                {watchCount > 0 && <span style={{ fontSize: 9, color: '#f59e0b' }}>→</span>}
            </div>

            {/* Open Bugs — clickable */}
            <div
                style={{ ...pillBase, background: bugBg, cursor: onTabChange ? 'pointer' : 'default' }}
                onClick={() => openBugs > 0 && onTabChange && onTabChange('feedback-insights')}
                title={openBugs > 0 ? 'Open bugs · click to review in Signals & Insights' : 'No open bugs'}
            >
                <Bug size={13} color={bugColor} />
                <span style={{ fontSize: 10, color: '#94a3b8' }}>Bugs</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: bugColor }}>{openBugs}</span>
                {openBugs > 0 && <span style={{ fontSize: 9, color: '#ef4444' }}>→</span>}
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.08)' }} />

            {/* Active users */}
            <div style={{ ...pillBase, background: 'rgba(129,140,248,0.08)' }}>
                <Users size={13} color="#818cf8" />
                <span style={{ fontSize: 10, color: '#94a3b8' }}>Active</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#818cf8' }}>{data.active_users}</span>
            </div>

            {/* Lessons */}
            <div style={{ ...pillBase, background: 'rgba(52,211,153,0.08)' }}>
                <Zap size={13} color="#34d399" />
                <span style={{ fontSize: 10, color: '#94a3b8' }}>Lessons</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#34d399' }}>{data.lessons_generated}</span>
            </div>

            <span style={{ fontSize: 10, color: '#334155', marginLeft: 'auto', fontFamily: 'monospace', flexShrink: 0 }}>
                LIVE · user_activity_daily
            </span>
        </div>
    );
};

// eslint-disable-next-line no-unused-vars
const StripItem = ({ icon: Icon, label, value, color = '#94a3b8', mono = false, title }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title={title || undefined}>
        <Icon size={12} style={{ color: '#475569' }} />
        <span style={{ fontSize: 10, color: '#475569' }}>{label}:</span>
        <span style={{
            fontSize: 12, fontWeight: 700, color,
            fontFamily: mono ? 'monospace' : 'inherit',
        }}>
            {value ?? '—'}
        </span>
    </div>
);


// ─── Filter Input ───────────────────────────────────────────────────────────

const FilterInput = ({ placeholder, value, onChange, width = 100 }) => (
    <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
            width, padding: '5px 8px', borderRadius: 6,
            border: '1px solid #334155', background: '#1e293b',
            color: '#cbd5e1', fontSize: 11, outline: 'none',
        }}
        onFocus={e => { e.target.style.borderColor = '#6366f1'; }}
        onBlur={e => { e.target.style.borderColor = '#334155'; }}
    />
);


export default AdminAnalyticsDashboard;
